import type { ClinicRequestContent } from "@/api/generated/models";
import { TextSendMessageRequestType as SendMessageRequestType } from "@/api/generated/models";
import {
  useIsChatMutationPending,
  useSendNoriMessage,
} from "@/api/mutations/conversation";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import Nudge from "@/components/Nudge";
import { Paragraph } from "@/components/Typography";
import { useInfiniteMessages } from "@/hooks/useInfiniteMessages";
import {
  useHasTalkToDoctorAiUpsellStoreHydrated,
  useTalkToDoctorAiUpsellStore,
  useUseCaseStore,
} from "@/store";
import { AssistantMessageTypes } from "@/types";

interface TalkToDoctorAINudgeProps {
  conversationId: string;
}

/**
 * Offers AI-driven intake for talk-to-a-doctor conversations. Shown above
 * the chat input. While the nudge is the active prompt, the CuraiRedirect
 * card hides itself (see CuraiRedirect.tsx) so the user sees one prompt at
 * a time. Yes → BE streams a canned welcome and Nori takes over. No / X →
 * the CuraiRedirect card appears for direct visit start.
 */
export default function TalkToDoctorAINudge({
  conversationId,
}: TalkToDoctorAINudgeProps) {
  const { value: isButtonEnabled, loading: isFlagLoading } = useFeatureFlag(
    "talk-to-doctor-button-enabled",
    true,
  );
  // When the talk-to-doctor button is disabled, the nudge is the only path to
  // start a visit so it can't be declined.
  const isMandatory = !isButtonEnabled;
  const conversationUseCase = useUseCaseStore((s) =>
    s.getConversationUseCase(conversationId),
  );
  const isTalkToDoctorConversation = conversationUseCase === "talk-to-a-doctor";

  const decision = useTalkToDoctorAiUpsellStore((s) =>
    s.getUpsellDecision(conversationId),
  );
  const setUpsellDecision = useTalkToDoctorAiUpsellStore(
    (s) => s.setUpsellDecision,
  );
  const hasHydrated = useHasTalkToDoctorAiUpsellStoreHydrated();

  const { sendNoriMessage } = useSendNoriMessage();
  const isPending = useIsChatMutationPending();
  const { data: messageData } = useInfiniteMessages(conversationId);

  const allMessages = messageData?.allMessages ?? [];
  let latestRedirectMessageId: string | null = null;
  for (let i = allMessages.length - 1; i >= 0; i--) {
    const msg = allMessages[i];
    if (
      msg.type === AssistantMessageTypes.CLINIC_REQUEST &&
      (msg.content as ClinicRequestContent | undefined)?.type === "VISIT"
    ) {
      latestRedirectMessageId = msg.id;
      break;
    }
  }

  if (
    !hasHydrated ||
    isFlagLoading ||
    !isTalkToDoctorConversation ||
    decision !== undefined ||
    isPending ||
    latestRedirectMessageId === null
  ) {
    return null;
  }

  const targetMessageId = latestRedirectMessageId;
  const handleAccept = () => {
    if (isPending) return;
    sendNoriMessage("", {
      type: SendMessageRequestType.talk_to_doctor_ai_upsell_accept,
      onSuccess: () =>
        setUpsellDecision(conversationId, {
          kind: "accepted",
          messageId: targetMessageId,
        }),
    });
  };
  const handleDecline = () => {
    if (isPending) return;
    setUpsellDecision(conversationId, { kind: "declined" });
  };

  const primaryAction = {
    onPress: handleAccept,
    dataTrackingId: "talk-to-doctor-ai-upsell-accept",
  };

  if (isMandatory) {
    return (
      <Nudge
        key="talk-to-doctor-ai-nudge"
        title="Let's save you some time"
        isDismissable={false}
        description={
          <Paragraph size="$sm" color="$textSecondary">
            Answer a few quick questions before your visit, saving you an
            average of 10 minutes.
          </Paragraph>
        }
        primaryAction={{ ...primaryAction, label: "Get started" }}
      />
    );
  }

  return (
    <Nudge
      key="talk-to-doctor-ai-nudge"
      title="Want a faster visit?"
      onDismiss={handleDecline}
      dismissTrackingId="talk-to-doctor-ai-upsell-dismiss"
      description={
        <Paragraph size="$sm" color="$textSecondary">
          Our AI can ask a few questions so your doctor isn't starting from
          scratch — saving an average of 10 minutes.
        </Paragraph>
      }
      primaryAction={{ ...primaryAction, label: "Yes, get started" }}
      secondaryAction={{
        label: "No thanks",
        onPress: handleDecline,
        dataTrackingId: "talk-to-doctor-ai-upsell-decline",
      }}
    />
  );
}
